import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Video, TrendingUp,
  UserMinus, UserX, Ban, Bookmark, Filter, Calendar, Users,
  ChevronRight, ChevronDown, BarChart3, RefreshCw, Grid3x3, RotateCcw,
  Inbox, ArrowRightLeft, Building2
} from 'lucide-react';
import toast from 'react-hot-toast';
import statisticsService from '../../services/statisticsService';
import { libraries as librariesApi } from '../../services/apiService';
import StatCard from '../Dashboard/StatCard';
import ProfileLink from '../UI/ProfileLink';
import DateRangePicker from '../UI/DateRangePicker';
import { Loader } from '../UI/LoadingSpinner';
import { Pagination, Autocomplete } from '@anyemp/ui-kit';
import { ChartCard, HorizontalBarChart, StackedBarChart } from '../../utils/chartComponents';
import { CHART_COLORS } from '../../utils/chartTheme';

// StatCard only accepts these fixed color names — cycled by stage index
// since the stage list itself is dynamic (see block1_funnel.by_stage).
const STAGE_CARD_COLORS = ['blue', 'purple', 'green', 'orange', 'yellow', 'red'];

// Дефолт — текущий календарный месяц (владелец: "по дефолту будет нынешний
// месяц выбран"), но дальше это свободный диапазон дат, не выбор месяца из
// списка — те же границы, что periodBounds() без аргументов на бэкенде.
const currentMonthRange = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const pad = (n) => String(n).padStart(2, '0');
  const start = `${y}-${pad(m + 1)}-01`;
  const lastDay = new Date(y, m + 1, 0).getDate();
  const end = `${y}-${pad(m + 1)}-${pad(lastDay)}`;
  return { start_date: start, end_date: end };
};

const formatDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// StatCard's `trend` prop expects a string starting with '+' or '-' to color
// it green/red — null/undefined renders no trend at all (nothing to compare
// against, e.g. previous month had 0).
const formatTrend = (deltaPct) => {
  if (deltaPct === null || deltaPct === undefined) return null;
  return deltaPct > 0 ? `+${deltaPct}%` : `${deltaPct}%`;
};

const formatDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// Table header cell with a hover tooltip explaining exactly how the column is
// computed — native `title`, no extra tooltip lib. Dotted underline signals
// "hover me" so it's not a silent affordance.
const Th = ({ children, hint, align = 'right', className = '' }) => (
  <th
    title={hint}
    className={`px-3 py-2 font-medium ${align === 'right' ? 'text-right' : 'text-left'} ${hint ? 'cursor-help decoration-dotted underline decoration-1 underline-offset-4' : ''} ${className}`}
  >
    {children}
  </th>
);

// Section wrapper — matches the panel style used across Statistics/*
const Panel = ({ title, icon: Icon, action, children, className = '' }) => (
  <div className={`min-w-0 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5 ${className}`}>
    {(title || action) && (
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="flex min-w-0 items-center gap-2 break-words text-lg font-semibold text-gray-900 dark:text-gray-100">
          {Icon && <Icon className="h-5 w-5 text-gray-500 dark:text-gray-400" />}
          {title}
        </h3>
        {action}
      </div>
    )}
    {children}
  </div>
);

const HrRecruitmentDashboard = () => {
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    ...currentMonthRange(),
    profession_id: '',
    hr_user_id: ''
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [professions, setProfessions] = useState([]);
  const [candidatesPage, setCandidatesPage] = useState(1);
  const [expandedCandidateId, setExpandedCandidateId] = useState(null);
  const [expandedExitSections, setExpandedExitSections] = useState({ refused: false, denied: false, blacklisted: false });
  const [sessionView, setSessionView] = useState('matrix'); // 'matrix' | 'chart'
  const perPage = 20;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { start_date: filters.start_date, end_date: filters.end_date };
      if (filters.profession_id) params.profession_id = filters.profession_id;
      if (filters.hr_user_id) params.hr_user_id = filters.hr_user_id;
      const res = await statisticsService.getHrRecruitmentStats(params);
      // Gateway wraps aggregation result as { success, data: <payload> } — unwrap one more level
      setData(res?.data || res);
      setCandidatesPage(1);
    } catch (e) {
      setError(e.message || 'Failed to load HR recruitment stats');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    (async () => {
      try {
        const profRes = await librariesApi.getProfessions().catch(() => ({ data: [] }));
        const profList = profRes?.data?.data || profRes?.data || [];
        setProfessions(Array.isArray(profList) ? profList : []);
      } catch (e) {
        console.error('Failed to load professions', e);
        toast.error('Failed to load professions');
      }
    })();
  }, []);

  const b0 = data?.block0_job_applications;
  const b1 = data?.block1_funnel;
  const b2 = data?.block2_dropoff || [];
  const b3 = data?.block3_exits || { refused: [], denied: [], blacklisted: [] };
  const b4 = data?.block4_sessions || [];
  const b5 = data?.block5_candidates || [];
  const b6 = data?.block6_conversion || { pairs: [], overall: 0 };
  const b7 = data?.block7_department_breakdown || [];
  const stagesMeta = data?.stages || [];

  const funnelChartData = useMemo(() => b2.map((row, i) => ({
    name: row.stage,
    Entered: row.entered,
    fill: CHART_COLORS[i % CHART_COLORS.length]
  })), [b2]);

  // Полный список стадий из бекенда (в порядке stage_order), а не только те,
  // что реально засветились в b4 — иначе стадия без единого перехода за
  // период молча пропадала бы из матрицы/графика вместо явного "0 везде".
  const stagesInBlock4 = useMemo(() => stagesMeta.map((s) => s.name), [stagesMeta]);

  const sessionChartData = useMemo(() => {
    const byDate = {};
    b4.forEach(row => {
      if (!byDate[row.date]) byDate[row.date] = { name: row.date };
      byDate[row.date][row.stage] = row.count;
    });
    return Object.values(byDate).sort((a, b) => a.name.localeCompare(b.name));
  }, [b4]);

  const sessionBars = useMemo(() => stagesInBlock4.map((s, i) => ({
    key: s, name: s, color: CHART_COLORS[i % CHART_COLORS.length]
  })), [stagesInBlock4]);

  // Matrix data for Block 4: rows = dates, columns = stages, cells = count
  const sessionMatrix = useMemo(() => {
    const cellMap = {}; // `${date}|${stage}` → count
    const dateTotals = {};
    const stageTotals = {};
    let grandTotal = 0;
    b4.forEach(row => {
      const key = `${row.date}|${row.stage}`;
      cellMap[key] = (cellMap[key] || 0) + row.count;
      dateTotals[row.date] = (dateTotals[row.date] || 0) + row.count;
      stageTotals[row.stage] = (stageTotals[row.stage] || 0) + row.count;
      grandTotal += row.count;
    });
    const dates = Object.keys(dateTotals).sort();
    return { cellMap, dates, dateTotals, stageTotals, grandTotal };
  }, [b4]);

  const pagedCandidates = useMemo(
    () => b5.slice((candidatesPage - 1) * perPage, candidatesPage * perPage),
    [b5, candidatesPage]
  );

  const professionOptions = useMemo(
    () => professions.map(p => ({ value: String(p.id), label: p.name || p.profession_name || `#${p.id}` })),
    [professions]
  );

  const hrOptions = useMemo(
    () => (data?.hr_users_pool || []).map(u => ({
      value: String(u.id),
      label: u.name || u.email || `ID ${u.id}`
    })),
    [data]
  );

  // Fallback for hr_user_id → display name when the enriched hr_user is absent
  const hrUserName = useCallback((idOrUser) => {
    if (!idOrUser) return '—';
    if (typeof idOrUser === 'object') return idOrUser.name || idOrUser.email || `ID ${idOrUser.id}`;
    const u = (data?.hr_users_pool || []).find(x => String(x.id) === String(idOrUser));
    return u ? (u.name || u.email || `ID ${idOrUser}`) : `ID ${idOrUser}`;
  }, [data]);

  if (loading && !data) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader variant="component" size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
        {error}
        <button onClick={loadData} className="ml-3 underline text-sm">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="break-words text-2xl font-bold text-gray-900 dark:text-gray-100">
            HR Recruitment Funnel
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Period: <span className="font-medium text-gray-700 dark:text-gray-300">{(data?.filters?.start_date || filters.start_date)} – {(data?.filters?.end_date || filters.end_date)}</span>
            {data?.filters?.profession_id && <> · Profession filter active</>}
            {data?.filters?.hr_user_id && <> · HR: {hrUserName(data.filters.hr_user_id)}</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      {filtersOpen && (
        <Panel>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <DateRangePicker
              label="Period"
              startDate={filters.start_date}
              endDate={filters.end_date}
              onChange={({ startDate, endDate }) => setFilters(f => ({
                ...f,
                start_date: startDate || currentMonthRange().start_date,
                end_date: endDate || currentMonthRange().end_date
              }))}
              className="w-full"
            />
            <Autocomplete
              label="Profession"
              fullWidth
              clearable
              placeholder="All professions"
              noOptionsText="No professions found"
              value={filters.profession_id || null}
              onChange={(v) => setFilters(f => ({ ...f, profession_id: v || '' }))}
              options={professionOptions}
              sx={{ trigger: '!bg-white dark:!bg-gray-800 !border-gray-300 dark:!border-gray-600 hover:!border-gray-400 dark:hover:!border-gray-500' }}
            />
            <Autocomplete
              label="HR"
              fullWidth
              clearable
              placeholder="All HRs"
              noOptionsText="No HRs found"
              value={filters.hr_user_id || null}
              onChange={(v) => setFilters(f => ({ ...f, hr_user_id: v || '' }))}
              options={hrOptions}
              sx={{ trigger: '!bg-white dark:!bg-gray-800 !border-gray-300 dark:!border-gray-600 hover:!border-gray-400 dark:hover:!border-gray-500' }}
            />
          </div>
          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={() => setFilters({ ...currentMonthRange(), profession_id: '', hr_user_id: '' })}
              disabled={(() => {
                const def = currentMonthRange();
                return filters.start_date === def.start_date && filters.end_date === def.end_date
                  && !filters.profession_id && !filters.hr_user_id;
              })()}
              className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          </div>
        </Panel>
      )}

      {/* Block 0 — Job Applications intake, upstream of the talent pipeline.
          Owner on the 31.08 call: "джобапликейшн это отдельная сущность...
          я хочу трекать эти, потому что на этапе между емейлом и телеграмом
          отвалится как минимум половина кандидатов" — raw applications vs
          how many actually became a Talent. */}
      <Panel title="Job Applications" icon={Inbox}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            title="Applications received"
            value={b0?.received ?? 0}
            icon={Inbox}
            color="blue"
            trend={formatTrend(b0?.received_delta_pct)}
            trendLabel="vs last month"
          />
          <StatCard
            title="Converted to talent"
            value={b0?.converted ?? 0}
            icon={ArrowRightLeft}
            color="green"
            trend={formatTrend(b0?.converted_delta_pct)}
            trendLabel="vs last month"
          />
          <StatCard
            title="Conversion rate"
            value={`${b0?.conversion_pct ?? 0}%`}
            icon={TrendingUp}
            color="purple"
          />
        </div>
      </Panel>

      {/* Block 1 — Monthly Funnel Summary, one card per real stage (not a
          hard-coded status list) + the terminal outcomes that can happen on
          any stage. Adding a stage in Stage Management makes its card show
          up here automatically. */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {(b1?.by_stage || []).map((row, i) => (
          <StatCard
            key={row.stage_key}
            title={`Entered: ${row.stage}`}
            value={row.entered}
            icon={Video}
            color={STAGE_CARD_COLORS[i % STAGE_CARD_COLORS.length]}
            trend={formatTrend(row.entered_delta_pct)}
            trendLabel="vs last month"
          />
        ))}
        <StatCard title="Refused (candidate)"    value={b1?.refused_total ?? 0} icon={UserMinus} color="yellow" trend={formatTrend(b1?.refused_total_delta_pct)} trendLabel="vs last month" />
        <StatCard title="Denied (company)"       value={b1?.denied_total  ?? 0} icon={UserX}     color="red"    trend={formatTrend(b1?.denied_total_delta_pct)}  trendLabel="vs last month" />
        <StatCard title="Blacklisted"            value={b1?.blacklisted   ?? 0} icon={Ban}       color="red" />
        <StatCard title="In talent pool (Later)" value={b1?.in_later_pool ?? 0} icon={Bookmark}  color="purple" />
      </div>

      {/* Block 2 — Funnel Drop-off by Stage. Chart and table stacked (not
          side-by-side): the table has 9 columns, at half page width it always
          needed horizontal scrolling to see the later ones. Full width lets
          every column show at once on a normal desktop viewport. */}
      <Panel title="Entered per stage" icon={BarChart3}>
        {funnelChartData.length > 0 && funnelChartData.some(r => r.Entered > 0) ? (
          <HorizontalBarChart data={funnelChartData} dataKey="Entered" name="Entered" />
        ) : (
          <div className="flex items-center justify-center h-[200px] text-gray-400 dark:text-gray-500 text-sm">
            No data for this month
          </div>
        )}
      </Panel>

      <Panel title="Drop-off details" icon={TrendingUp}>
          <div className="-mx-4 overflow-x-auto sm:-mx-5">
            <table className="min-w-[900px] text-sm">
              <thead>
                <tr className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <Th align="left" className="px-5" hint="The pipeline stage (Video, Interview, ...), in stage_order.">Stage</Th>
                  <Th hint="Distinct candidates who got a stage_set or stage_changed event INTO this stage during the selected period.">Entered</Th>
                  <Th hint="Of those who entered, distinct candidates who later got a stage_changed event moving them FORWARD (to a stage with a higher stage_order) during the period.">Progressed</Th>
                  <Th hint="Distinct candidates whose status changed (or was set) to 'Approved' while their current stage is this one. Not a drop-off — informational only, not counted in Drop-off %.">Approved</Th>
                  <Th hint="Distinct candidates whose status changed (or was set) to 'Not Approved' while their current stage is this one.">Not appr.</Th>
                  <Th hint="Distinct candidates whose status changed (or was set) to 'Denied' — company rejected them — while their current stage is this one.">Denied</Th>
                  <Th hint="Distinct candidates whose status changed (or was set) to 'Refused' — candidate withdrew themselves — while their current stage is this one.">Refused</Th>
                  <Th hint="Distinct candidates whose status changed (or was set) to 'Not relevant for us' while their current stage is this one.">Not relevant</Th>
                  <Th align="left" className="px-5" hint="(Denied + Refused + Not appr. + Not relevant) / Entered × 100. Approved is excluded — it's not a drop.">Drop-off</Th>
                </tr>
              </thead>
              <tbody>
                {b2.length === 0 && (
                  <tr><td colSpan={9} className="text-center py-6 text-gray-400 dark:text-gray-500">No data</td></tr>
                )}
                {b2.map((r) => (
                  <tr key={r.stage} className="border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-5 py-2 font-medium text-gray-900 dark:text-gray-100">{r.stage}</td>
                    <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">{r.entered}</td>
                    <td className="px-3 py-2 text-right text-green-600 dark:text-green-400 font-medium">{r.progressed}</td>
                    <td className="px-3 py-2 text-right text-blue-600 dark:text-blue-400">{r.approved}</td>
                    <td className="px-3 py-2 text-right text-orange-600 dark:text-orange-400">{r.not_approved}</td>
                    <td className="px-3 py-2 text-right text-red-600 dark:text-red-400">{r.denied}</td>
                    <td className="px-3 py-2 text-right text-yellow-600 dark:text-yellow-400">{r.refused}</td>
                    <td className="px-3 py-2 text-right text-gray-500 dark:text-gray-400">{r.not_relevant}</td>
                    <td className="px-5 py-2 text-right font-semibold text-gray-900 dark:text-gray-100">{r.dropoff_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

      {/* Block 3 — Refusals / Denials / Blacklisted — full-width, collapsible with animation */}
      <div className="space-y-3">
        {[
          { key: 'refused',     title: 'Refused (candidate)', rows: b3.refused,     icon: UserMinus, accent: 'text-yellow-600 dark:text-yellow-400', hint: 'Candidate withdrew from the process' },
          { key: 'denied',      title: 'Denied (company)',    rows: b3.denied,      icon: UserX,     accent: 'text-red-600 dark:text-red-400',       hint: 'Company rejected the candidate' },
          { key: 'blacklisted', title: 'Blacklisted',         rows: b3.blacklisted, icon: Ban,       accent: 'text-gray-600 dark:text-gray-400',     hint: 'Candidate blacklisted permanently' }
        ].map(({ key, title, rows, icon: Icon, accent, hint }) => {
          const isOpen = expandedExitSections[key];
          const empty = rows.length === 0;
          return (
            <div key={key} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <button
                type="button"
                onClick={() => setExpandedExitSections(s => ({ ...s, [key]: !s[key] }))}
                disabled={empty}
                aria-expanded={isOpen}
                className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors ${empty ? 'cursor-default' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}
              >
                <Icon className={`h-5 w-5 shrink-0 ${accent}`} />
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>
                </div>
                <span className={`text-2xl font-bold tabular-nums ${accent}`}>{rows.length}</span>
                {!empty && (
                  <ChevronRight
                    className={`h-5 w-5 text-gray-400 dark:text-gray-500 shrink-0 transition-transform duration-300 ease-out ${isOpen ? 'rotate-90' : ''}`}
                  />
                )}
              </button>

              {/* Animated collapse: grid-rows [0fr → 1fr] animates height without knowing content size */}
              <div
                className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen && !empty ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
                aria-hidden={!isOpen || empty}
              >
                <div className="overflow-x-auto overscroll-x-contain">
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    <table className="min-w-[720px] w-full text-sm table-fixed">
                      <thead>
                        <tr className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30">
                          <th className="text-left px-5 py-2 font-medium w-1/4">Candidate</th>
                          <th className="text-left px-3 py-2 font-medium w-32">Stage</th>
                          <th className="text-left px-3 py-2 font-medium w-32">Date</th>
                          <th className="text-left px-3 py-2 font-medium">Reason</th>
                          <th className="text-left px-5 py-2 font-medium w-48">HR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={`${r.talent_id}-${r.date}-${i}`} className="border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/50 align-top">
                            <td className="px-5 py-2">
                              <ProfileLink to={`/talents/${r.talent_id}`} state={{ from: location.pathname + location.search }}>
                                {r.talent_name}
                              </ProfileLink>
                              {r.profession && <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{r.profession}</div>}
                            </td>
                            <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{r.stage || '—'}</td>
                            <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(r.date)}</td>
                            <td className="px-3 py-2 text-gray-700 dark:text-gray-300 break-words">
                              {r.reason ? <span className="italic">“{r.reason}”</span> : <span className="text-gray-400 dark:text-gray-500">—</span>}
                            </td>
                            <td className="px-5 py-2 text-gray-500 dark:text-gray-400 truncate">{hrUserName(r.hr_user || r.hr_user_id)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Block 4 — Session Breakdown (matrix + chart toggle) */}
      <Panel
        title="Transitions per day, grouped by stage"
        icon={Calendar}
        action={
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-0.5">
            <button
              type="button"
              onClick={() => setSessionView('matrix')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                sessionView === 'matrix'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
              aria-pressed={sessionView === 'matrix'}
            >
              <Grid3x3 className="h-3.5 w-3.5" /> Matrix
            </button>
            <button
              type="button"
              onClick={() => setSessionView('chart')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                sessionView === 'chart'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
              aria-pressed={sessionView === 'chart'}
            >
              <BarChart3 className="h-3.5 w-3.5" /> Chart
            </button>
          </div>
        }
      >
        {sessionMatrix.dates.length === 0 ? (
          <div className="flex items-center justify-center h-[280px] text-gray-400 dark:text-gray-500 text-sm">
            No transitions this month
          </div>
        ) : sessionView === 'matrix' ? (
          <div className="-mx-4 overflow-hidden rounded-b-lg border-t border-gray-200 dark:border-gray-700 sm:-mx-5">
            <div className="overflow-x-auto">
              <table className="min-w-max text-sm border-separate border-spacing-0">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                    <th className="sticky left-0 z-20 min-w-[140px] border-r border-gray-200 bg-gray-50 px-3 py-3 text-left font-semibold text-gray-600 shadow-[4px_0_6px_-5px_rgba(0,0,0,0.45)] dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 sm:min-w-[160px] sm:px-4">
                      Stage
                    </th>
                    {sessionMatrix.dates.map((date) => {
                      const d = new Date(date);
                      const isWeekend = d.getUTCDay() === 0 || d.getUTCDay() === 6;
                      const dayNum = d.getUTCDate();
                      const weekday = d.toLocaleDateString(undefined, { weekday: 'short' });
                      return (
                        <th
                          key={date}
                          className={`px-2 py-3 text-center border-r border-gray-200 dark:border-gray-600 min-w-[46px] ${isWeekend ? 'bg-gray-100 dark:bg-gray-600' : ''}`}
                        >
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="font-semibold text-gray-600 dark:text-gray-300 text-xs">{dayNum}</span>
                            <span className="text-[10px] text-gray-400 leading-none">{weekday}</span>
                          </div>
                        </th>
                      );
                    })}
                    <th className="px-3 py-3 text-center font-bold text-white bg-teal-600 min-w-[70px] whitespace-nowrap">
                      TOTAL
                    </th>
                  </tr>
                </thead>

                <tbody className="[&>tr>td]:border-b [&>tr>td]:border-gray-100 dark:[&>tr>td]:border-gray-700">
                  {stagesInBlock4.map((stage) => (
                    <tr key={stage} className="group hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors">
                      <td className="sticky left-0 z-10 min-w-[140px] border-r border-gray-200 bg-white px-3 py-3 shadow-[4px_0_6px_-5px_rgba(0,0,0,0.45)] transition-colors group-hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:group-hover:bg-[#2d3747] sm:min-w-[160px] sm:px-4">
                        <span className="font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">{stage}</span>
                      </td>
                      {sessionMatrix.dates.map((date) => {
                        const d = new Date(date);
                        const isWeekend = d.getUTCDay() === 0 || d.getUTCDay() === 6;
                        const v = sessionMatrix.cellMap[`${date}|${stage}`] || 0;
                        return (
                          <td
                            key={date}
                            className={`px-2 py-3 text-center border-r border-gray-100 dark:border-gray-700 tabular-nums ${
                              isWeekend ? 'bg-gray-50 dark:bg-gray-700/30' : ''
                            } ${
                              v > 0
                                ? 'font-semibold text-gray-900 dark:text-gray-100'
                                : 'text-gray-300 dark:text-gray-600'
                            }`}
                          >
                            {v > 0 ? v : '—'}
                          </td>
                        );
                      })}
                      <td className="px-3 py-3 text-center font-bold text-white bg-teal-600">
                        {sessionMatrix.stageTotals[stage] || 0}
                      </td>
                    </tr>
                  ))}
                </tbody>

                <tfoot>
                  <tr className="border-t-2 border-teal-500 bg-teal-50 dark:bg-teal-900/20">
                    <td className="sticky left-0 z-10 min-w-[140px] whitespace-nowrap border-r border-gray-200 bg-teal-50 px-3 py-3 font-bold text-teal-700 shadow-[4px_0_6px_-5px_rgba(0,0,0,0.45)] dark:border-gray-600 dark:bg-[#1b343d] dark:text-teal-300 sm:min-w-[160px] sm:px-4">
                      Total
                    </td>
                    {sessionMatrix.dates.map((date) => {
                      const d = new Date(date);
                      const isWeekend = d.getUTCDay() === 0 || d.getUTCDay() === 6;
                      const t = sessionMatrix.dateTotals[date] || 0;
                      return (
                        <td
                          key={date}
                          className={`px-2 py-3 text-center border-r border-gray-100 dark:border-gray-700 font-semibold tabular-nums ${
                            isWeekend ? 'bg-teal-100/50 dark:bg-teal-900/40' : ''
                          } ${t > 0 ? 'text-teal-700 dark:text-teal-300' : 'text-gray-300 dark:text-gray-600'}`}
                        >
                          {t > 0 ? t : '—'}
                        </td>
                      );
                    })}
                    <td className="px-3 py-3 text-center font-bold text-white bg-teal-700">
                      {sessionMatrix.grandTotal}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ) : (
          <StackedBarChart data={sessionChartData} bars={sessionBars} height={340} />
        )}
      </Panel>

      {/* Block 5 — Candidate Detail Table */}
      <Panel
        title="Candidates Active This Month"
        icon={Users}
        action={<span className="text-sm text-gray-500 dark:text-gray-400">{b5.length} total</span>}
      >
        <div className="-mx-4 overflow-x-auto sm:-mx-5">
          <table className="min-w-[720px] w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <th className="w-8 px-3 py-2"></th>
                <th className="text-left px-3 py-2 font-medium">Candidate</th>
                <th className="text-left px-3 py-2 font-medium">Profession</th>
                <th className="text-left px-3 py-2 font-medium">Type</th>
                <th className="text-left px-3 py-2 font-medium">Current stage</th>
                <th className="text-right px-5 py-2 font-medium">Transitions</th>
              </tr>
            </thead>
            <tbody>
              {pagedCandidates.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-gray-400 dark:text-gray-500">No candidates matched the filters</td></tr>
              )}
              {pagedCandidates.map((r) => {
                const isOpen = expandedCandidateId === r.talent_id;
                const toggle = () => setExpandedCandidateId(isOpen ? null : r.talent_id);
                return (
                  <React.Fragment key={r.talent_id}>
                    <tr
                      onClick={toggle}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
                      tabIndex={0}
                      role="button"
                      aria-expanded={isOpen}
                      className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-800/50"
                    >
                      <td className="px-3 py-2">
                        <ChevronRight
                          className={`h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform duration-200 ease-out ${isOpen ? 'rotate-90' : ''}`}
                        />
                      </td>
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <ProfileLink to={`/talents/${r.talent_id}`} state={{ from: location.pathname + location.search }}>
                          {r.talent_name}
                        </ProfileLink>
                      </td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.profession || '—'}</td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{r.talent_type || '—'}</td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{r.current_stage_name || '—'}</td>
                      <td className="px-5 py-2 text-right text-gray-900 dark:text-gray-100 font-medium">{r.transitions.length}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-50 dark:bg-gray-800/50">
                        <td colSpan={6} className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                          <p className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Stage &amp; status history</p>
                          <ol className="space-y-1">
                            {r.transitions.map((t, i) => (
                              <li key={i} className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">{formatDateTime(t.at)}</span>
                                <span className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
                                  t.kind === 'stage'
                                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                                }`}>
                                  {t.kind === 'stage' ? 'Stage' : 'Status'}
                                </span>
                                <span className="font-medium">{t.from || '∅'}</span>
                                <ChevronRight className="h-3 w-3 text-gray-400" />
                                <span className="font-medium">{t.to}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">· HR: {hrUserName(t.hr_user || t.hr_user_id)}</span>
                                {t.reason && <span className="text-xs text-gray-500 dark:text-gray-400">· “{t.reason}”</span>}
                              </li>
                            ))}
                          </ol>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {b5.length > perPage && (
          <div className="min-w-0 pt-2">
            <Pagination
              currentPage={candidatesPage}
              totalPages={Math.ceil(b5.length / perPage)}
              totalItems={b5.length}
              itemsPerPage={perPage}
              onPageChange={setCandidatesPage}
              showResults="range"
              sx="p-0"
            />
          </div>
        )}
      </Panel>

      {/* Block 6 — Conversion Rates, between consecutive real stages (not a
          fixed list of 6 status pairs — a new stage in the pipeline gets its
          own pair automatically). */}
      <Panel title="Conversion Rates" icon={TrendingUp}>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {b6.pairs.map((row) => (
            <div key={`${row.from}-${row.to}`} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">{row.from} → {row.to}</p>
              <p className="text-2xl font-bold mt-1 text-gray-900 dark:text-gray-100">{row.pct}%</p>
              <p className="text-[11px] mt-1 text-gray-400 dark:text-gray-500 tabular-nums">{row.num ?? 0} / {row.den ?? 0}</p>
            </div>
          ))}
          <div className="rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 p-4">
            <p className="text-xs font-medium text-teal-700 dark:text-teal-400 truncate">Overall (first → last stage)</p>
            <p className="text-2xl font-bold mt-1 text-teal-900 dark:text-teal-200">{b6.overall}%</p>
          </div>
        </div>
      </Panel>

      {/* Block 7 — Entries per stage, by department. Owner on 28.08:
          "по департаментам, на какие стадии... чем более подробно, тем лучше". */}
      <Panel title="Entries by Department" icon={Building2}>
        {b7.length === 0 ? (
          <div className="flex items-center justify-center h-[120px] text-gray-400 dark:text-gray-500 text-sm">
            No data for this month
          </div>
        ) : (
          <div className="-mx-4 overflow-x-auto sm:-mx-5">
            <table className="min-w-max text-sm">
              <thead>
                <tr className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left px-5 py-2 font-medium">Department</th>
                  {stagesMeta.map((s) => (
                    <th key={s.key} className="text-right px-3 py-2 font-medium whitespace-nowrap">{s.name}</th>
                  ))}
                  <th className="text-right px-5 py-2 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {b7.map((row) => (
                  <tr key={row.department_id ?? row.department} className="border-b border-gray-100 dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-5 py-2 font-medium text-gray-900 dark:text-gray-100">{row.department}</td>
                    {row.by_stage.map((cell) => (
                      <td key={cell.stage_key} className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{cell.count || '—'}</td>
                    ))}
                    <td className="px-5 py-2 text-right font-semibold text-gray-900 dark:text-gray-100">{row.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
};

export default HrRecruitmentDashboard;
